// frontend/src/context/CurrencyContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

// Exchange rates with SLL as primary (Sierra Leone Leone)
const DEFAULT_RATES = {
  USD: 1,
  SLL: 23500, // 1 USD = 23,500 SLL
  MYR: 4.75, // 1 USD = 4.75 Malaysian Ringgit
};

// Currency configurations
const CURRENCY_CONFIGS = {
  SLL: {
    code: "SLL",
    symbol: "Le",
    label: "Sierra Leone Leone",
    isPrimary: true,
    locale: "en-SL",
  },
  USD: {
    code: "USD",
    symbol: "$",
    label: "US Dollar",
    isPrimary: false,
    locale: "en-US",
  },
  MYR: {
    code: "MYR",
    symbol: "RM",
    label: "Malaysian Ringgit",
    isPrimary: false,
    locale: "ms-MY",
  },
};

export const CurrencyProvider = ({ children }) => {
  // Default currency is SLL (Sierra Leone Leone) - the primary business currency
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem("preferredCurrency");
    return saved && ["USD", "SLL", "MYR"].includes(saved) ? saved : "SLL";
  });
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Save currency preference
  useEffect(() => {
    localStorage.setItem("preferredCurrency", currency);
  }, [currency]);

  // Fetch live exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(
          "https://api.exchangerate-api.com/v4/latest/USD",
          { cache: "no-cache" },
        );
        const data = await response.json();
        if (data.rates) {
          setRates({
            USD: 1,
            SLL: data.rates.SLL || DEFAULT_RATES.SLL,
            MYR: data.rates.MYR || DEFAULT_RATES.MYR,
          });
          setLastUpdated(new Date());
        }
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
        // Keep using default rates
      } finally {
        setLoading(false);
      }
    };
    fetchRates();

    // Refresh rates every hour
    const interval = setInterval(fetchRates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Convert USD amount to current currency
   */
  const convertPrice = (amountUSD) => {
    if (!amountUSD && amountUSD !== 0) return 0;
    const rate = rates[currency] || 1;
    return amountUSD * rate;
  };

  /**
   * Convert from current currency to USD
   */
  const convertFromCurrent = (amount) => {
    if (!amount && amount !== 0) return 0;
    const rate = rates[currency] || 1;
    return amount / rate;
  };

  /**
   * Convert SLL to USD
   */
  const convertFromSLL = (amountSLL) => {
    if (!amountSLL && amountSLL !== 0) return 0;
    return amountSLL / rates.SLL;
  };

  /**
   * Format price with currency symbol
   */
  const formatPrice = (amount, overrideCurrency = null) => {
    const targetCurrency = overrideCurrency || currency;
    const config = CURRENCY_CONFIGS[targetCurrency];
    if (!config) return `Le${amount.toLocaleString()}`;

    const symbol = config.symbol;
    const formatted = amount.toLocaleString(config.locale || "en-SL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return `${symbol}${formatted}`;
  };

  /**
   * Get currency symbol
   */
  const getSymbol = (currencyCode = null) => {
    const targetCurrency = currencyCode || currency;
    const config = CURRENCY_CONFIGS[targetCurrency];
    return config?.symbol || "Le";
  };

  /**
   * Get exchange rate for current currency
   */
  const getExchangeRateForDisplay = () => {
    return rates[currency] || 1;
  };

  /**
   * Get all prices in all currencies
   */
  const getAllPrices = (amountUSD) => {
    return {
      usd: amountUSD,
      sll: amountUSD * rates.SLL,
      myr: amountUSD * rates.MYR,
    };
  };

  /**
   * Format all prices
   */
  const formatAllPrices = (amountUSD) => {
    const prices = getAllPrices(amountUSD);
    return {
      usd: formatPrice(prices.usd, "USD"),
      sll: formatPrice(prices.sll, "SLL"),
      myr: formatPrice(prices.myr, "MYR"),
    };
  };

  /**
   * Get available currencies
   */
  const getAvailableCurrencies = () => {
    return Object.values(CURRENCY_CONFIGS);
  };

  /**
   * Check if a currency is the primary currency
   */
  const isPrimaryCurrency = (currencyCode = null) => {
    const targetCurrency = currencyCode || currency;
    return CURRENCY_CONFIGS[targetCurrency]?.isPrimary || false;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        rates,
        convertPrice,
        convertFromCurrent,
        convertFromSLL,
        formatPrice,
        getSymbol,
        getExchangeRateForDisplay,
        getAllPrices,
        formatAllPrices,
        loading,
        lastUpdated,
        defaultCurrency: "SLL",
        getAvailableCurrencies,
        isPrimaryCurrency,
        CURRENCY_CONFIGS,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};
